// Adds the signed-in user's Firebase ID token to calls to our gated Cloud
// Functions (functions/utils/httpGate.js), so every screen sends it without
// threading headers through each fetch. Installed once in index.js.
//
// Only routes in GATED_ROUTES get the header: other endpoints' CORS may not
// allow Authorization, and adding it there would fail their preflight.

export const FUNCTIONS_ORIGIN = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net/';

// Must match the exported names that call gate() in functions/.
export const GATED_ROUTES = new Set([
  // search
  'nlOdds', 'nlOddsFromImage',
  // signed-in user
  'getAllCachedFixtures', 'getCachedSGPs', 'queryCachedFixtures', 'queryCachedSGPs',
  'uploadUserImage', 'opticOddsProxy',
  // admin
  'generateSGPs', 'getBestBets', 'getCacheStats', 'getCachedFixture', 'getFixturesFromOddsMarkets',
  'getMarketOdds', 'getPlayerProps', 'getSGPCacheAggregateStats', 'getSGPGenerationProgress',
  'getSgpHealth', 'runRealDataTests',
  'analyzeStraightsCache', 'backfillSGPMetadata', 'cleanupEndedSGPs', 'debugOddsMarkets',
  'discoverLeagues', 'dissectPlayerProp', 'fullCacheSync', 'getCacheLogs', 'getFeedbackInsights',
  'getHealthInsights', 'getNotificationPreferences', 'getPlayerPropsCacheHealth',
  'getPlayerPropsCacheStats', 'getPlayersCacheStats', 'getQueryAnalytics', 'getRefreshStatus',
  'getSGPCoverageReport', 'mathValidation', 'opticOddsSweep', 'playerPropsCacheHealth',
  'previewSignupWelcomeEmail', 'processSGPTaskHTTP', 'quickRefresh', 'refreshAllSGPs',
  'refreshAllSGPsSports', 'refreshAllTiers', 'refreshBestBets', 'refreshCollegeSports',
  'refreshInjuries', 'refreshOddsCache', 'refreshOnDemand', 'refreshPlayerProps',
  'refreshPlayerPropsCache', 'refreshPlayersCache', 'resetFailedSGPs', 'resetSGPBlocks',
  'runQueryTests', 'sendHealthReport', 'sendTestSignupWelcomeEmail', 'setAppMetadata',
  'syncSGPCache', 'testSingleQuery', 'triggerAutoGenerateSGPs', 'triggerCacheRefresh',
  'updateFeedbackStatus', 'updateNotificationPreferences',
  'generateMarketAuditReport', 'getMarketAuditSnapshot', 'getMarketAuditDownload',
  'runMarketAuditHTTP', 'runLiveDataTests',
]);

// What to tell the user when the gate refuses a call ({ reason } in the JSON body).
const GATE_MESSAGES = {
  auth_required: 'Sign in to search. BetOptimized is in invite-only beta.',
  auth_invalid: 'Your sign-in expired. Sign in again to keep searching.',
  launch_gate: "You're on the list. BetOptimized is invite-only right now, and we'll email you when your access opens.",
  suspended: 'This account is suspended. Contact support if you think this is a mistake.',
  quota: "You've used all your searches for this period. Upgrade your plan for more.",
  admin_required: 'Admin access required.',
};

/** Friendly message for a gate refusal (401/403/429 with a known reason), else null. */
export function gateErrorMessage(status, bodyText) {
  if (![401, 403, 429].includes(Number(status))) return null;
  try {
    const reason = JSON.parse(bodyText || '{}')?.reason;
    return GATE_MESSAGES[reason] || null;
  } catch {
    return null;
  }
}

// Requests made while Firebase is still restoring the session wait this long for it.
export const AUTH_READY_WAIT_MS = 5000;

/** Route name for a functions URL ("nlOdds" for …/nlOdds?x=1), else null. */
export function gatedRoute(url) {
  const s = String(url || '');
  if (!s.startsWith(FUNCTIONS_ORIGIN)) return null;
  const route = s.slice(FUNCTIONS_ORIGIN.length).split(/[/?#]/)[0];
  return GATED_ROUTES.has(route) ? route : null;
}

function hasAuthHeader(headers) {
  if (!headers) return false;
  if (typeof headers.has === 'function') return headers.has('Authorization');
  return Object.keys(headers).some((k) => k.toLowerCase() === 'authorization');
}

function withHeader(headers, token) {
  if (headers && typeof headers.set === 'function') {
    const h = new Headers(headers);
    h.set('Authorization', `Bearer ${token}`);
    return h;
  }
  return { ...(headers || {}), Authorization: `Bearer ${token}` };
}

async function currentToken(auth) {
  let timer;
  try {
    if (typeof auth.authStateReady === 'function') {
      await Promise.race([auth.authStateReady(), new Promise((r) => { timer = setTimeout(r, AUTH_READY_WAIT_MS); })]);
    }
    return auth.currentUser ? await auth.currentUser.getIdToken() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Wraps a fetch so gated function calls carry the user's ID token. */
export function makeAuthFetch(baseFetch, auth) {
  return async function authFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url;
    if (!gatedRoute(url) || hasAuthHeader(init.headers) || (typeof input !== 'string' && hasAuthHeader(input?.headers))) {
      return baseFetch(input, init);
    }
    const token = await currentToken(auth);
    if (!token) return baseFetch(input, init);
    return baseFetch(input, { ...init, headers: withHeader(init.headers, token) });
  };
}

let _installed = false;

/** Patches globalThis.fetch once (web and native). */
export function installAuthFetch(auth) {
  if (_installed || typeof globalThis.fetch !== 'function' || !auth) return;
  globalThis.fetch = makeAuthFetch(globalThis.fetch.bind(globalThis), auth);
  _installed = true;
}
